if (!customElements.get('testimonials-component')) {
  class Testimonials extends HTMLElement {
    constructor() {
      super();
      this.sliderInstance = null;
    }

    connectedCallback() {
      this.cacheDom();
      this.updateAccessibility();

      if (!this.enableSlider) return;

      this.init();

      document.addEventListener('matchMobile', this.init.bind(this));
      document.addEventListener('unmatchMobile', this.init.bind(this));
    }

    // Cache DOM once
    cacheDom() {
      this.sectionId = this.dataset.sectionId;
      this.section = this.closest(`.section-${this.sectionId}`);

      this.selectors = {
        sliderWrapper: '.testimonials__items',
        nextEl: '.swiper-btn-next',
        prevEl: '.swiper-btn-prev',
      };

      this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);

      this.enableSlider = this.dataset.enableSlider === 'true';
      this.items = +this.dataset.items;
      this.tabletItems = +this.dataset.tabletItems;

      this.totalItems = +this.dataset.totalItems || 0;

      const statusId = this.dataset.statusTarget;
      this.statusEl = statusId ? this.section?.querySelector(`#${statusId}`) : null;

      this.classes = {
        grid: 'f-grid',
        swiper: 'swiper',
        swiperWrapper: 'swiper-wrapper',
      };
    }

    init() {
      FoxTheme.config.mqlMobile ? this.destroySlider() : this.initSlider();
    }

    initSlider() {
      if (this.sliderInstance) return;

      const gap = parseFloat(
        window.getComputedStyle(this.sliderWrapper).getPropertyValue('--column-gap')
      ) * 10;

      const sliderOptions = {
        slidesPerView: 2,
        spaceBetween: gap,
        navigation: {
          nextEl: this.section.querySelector(this.selectors.nextEl),
          prevEl: this.section.querySelector(this.selectors.prevEl),
        },
        watchSlidesProgress: true,
        breakpoints: {
          768: { slidesPerView: Math.min(this.tabletItems, 3) },
          1280: { slidesPerView: this.items },
        },
        loop: true,
        threshold: 2,
      };

      this.classList.add(this.classes.swiper);
      this.sliderWrapper.classList.replace(this.classes.grid, this.classes.swiperWrapper);

      this.sliderInstance = new window.FoxTheme.Carousel(this, sliderOptions);
      this.sliderInstance.init();

      // Events
      const { slider } = this.sliderInstance;

      slider.on('slideChangeTransitionEnd', this.updateAccessibility.bind(this));
      slider.on('resize', this.updateAccessibility.bind(this));

      // Shopify Editor
      if (Shopify.designMode) {
        document.addEventListener('shopify:block:select', e => {
          if (e.detail.sectionId !== this.sectionId) return;
          this.sliderInstance.slider.slideToLoop(+e.target.dataset.index);
        });
      }

      this.updateAccessibility();
    }

    destroySlider() {
      if (!this.sliderInstance) return;

      const { slider } = this.sliderInstance;
      slider.destroy();
      this.sliderInstance = null;

      this.classList.remove(this.classes.swiper);
      this.sliderWrapper.classList.replace(this.classes.swiperWrapper, this.classes.grid);

      this.updateAccessibility();
    }

    // Combines tabindex + aria-hidden + status update
    updateAccessibility() {
      this.updateProductTabIndex();
      this.updateSlideAriaHidden();
      this.updateSlideStatus();
    }

    updateProductTabIndex() {
      const links = this.querySelectorAll('.testimonial__product');
      if (!links.length) return;

      const sliderActive = this.enableSlider && this.sliderInstance;
      if (!sliderActive) {
        links.forEach(l => l.tabIndex = 0);
        return;
      }

      const slides = [...this.sliderWrapper.querySelectorAll('.swiper-slide')];
      const activeIndex = slides.findIndex(s => s.classList.contains('swiper-slide-active'));

      slides.forEach((slide, index) => {
        const isVisible = this.isVisible(index, activeIndex, slides.length);
        slide.querySelectorAll('.testimonial__product').forEach(link => {
          link.tabIndex = isVisible ? 0 : -1;
        });
      });
    }

    updateSlideAriaHidden() {
      const slides = [...this.sliderWrapper.querySelectorAll('.f-column')];
      if (!slides.length) return;

      const sliderActive = this.enableSlider && this.sliderInstance;
      if (!sliderActive) {
        slides.forEach(s => s.removeAttribute('aria-hidden'));
        return;
      }

      const activeIndex = slides.findIndex(s => s.classList.contains('swiper-slide-active'));

      slides.forEach((slide, index) => {
        slide.setAttribute('aria-hidden', this.isVisible(index, activeIndex, slides.length) ? 'false' : 'true');
      });
    }

    updateSlideStatus() {
      if (!this.statusEl || !this.totalItems || !this.sliderInstance) return;

      const current = (this.sliderInstance.slider.realIndex || 0) + 1;
      this.statusEl.textContent = `Current slide ${current} of ${this.totalItems} activated`;
    }

    isVisible(index, activeIndex, total) {
      if (total <= 0) return false;
      return ((index - activeIndex + total) % total) <= 2;
    }
  }

  customElements.define('testimonials-component', Testimonials);
}
